/**
 * Grid Action Registry
 *
 * Allows external plugins to register custom button actions for the grid control.
 * The built-in actions (add, deleteSelected, editSelected, addQuickItems) are
 * handled directly in GridControl; every unknown action is dispatched here.
 *
 * Open/Closed Principle: GridControl never changes to support new actions —
 * plugins extend behaviour by registering handlers.
 *
 * Usage in a plugin:
 *
 *   import { GridActionRegistry, stampRowId } from '@atlas-forms/player-components-react';
 *
 *   // Side-effect-only action (no row change) — return null/void:
 *   GridActionRegistry.register('exportToPowerPoint', ({ rows, config }) => {
 *     exportRows(rows, config);
 *     return null;
 *   });
 *
 *   // Action that mutates rows — return the new rows array:
 *   GridActionRegistry.register('duplicateSelected', ({ rows, selectedIds }) => {
 *     const copies = [...rows]
 *       .filter(r => selectedIds.has(r._rowId))
 *       .map(r => stampRowId({ ...r }));
 *     return [...rows, ...copies];
 *   });
 */

import type { GridRow, GridConfig, GridButton } from './grid.types';

// ─── Context passed to every action handler ───────────────────────────────────

export interface GridActionContext {
  /** Current rows snapshot (read-only — return a new array to mutate) */
  readonly rows: GridRow[];
  /** IDs of currently selected rows */
  readonly selectedIds: ReadonlySet<string>;
  /** Full grid config at the time of the action */
  readonly config: GridConfig;
  /** The button definition that triggered the action */
  readonly button: GridButton;
}

/**
 * Handler signature.
 *
 * Return the new rows array if the action changes rows.
 * Return null / undefined if the action is side-effect-only (export, open dialog, etc.).
 * The grid will call setRows + emitChange only when a non-null array is returned.
 */
export type GridActionHandler = (
  ctx: GridActionContext,
) => GridRow[] | null | undefined | void;

// ─── Registry ─────────────────────────────────────────────────────────────────

const _handlers = new Map<string, GridActionHandler>();

export const GridActionRegistry = {
  /**
   * Register a handler for a custom button action.
   * Overwrites any previously registered handler for the same action name.
   */
  register(action: string, handler: GridActionHandler): void {
    _handlers.set(action, handler);
  },

  /** Look up a handler by action name. Returns undefined if not registered. */
  get(action: string): GridActionHandler | undefined {
    return _handlers.get(action);
  },

  /** Returns true if a handler is registered for the given action name. */
  has(action: string): boolean {
    return _handlers.has(action);
  },

  /** Remove a previously registered handler. */
  unregister(action: string): void {
    _handlers.delete(action);
  },

  /** Remove all registered handlers (useful for test teardown). */
  clear(): void {
    _handlers.clear();
  },
} as const;
